'use client'

import { cn, Popover, PopoverContent, PopoverTrigger, Separator, Skeleton, Text } from '@repo/ui'
import { AnimatePresence, motion } from 'framer-motion'
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
  useRef,
  useState
} from 'react'

/**
 * Unified hover card primitive for web3 entities (addresses, tokens, markets, …).
 *
 * Built on the `Popover` primitive with a manual hover-open / hover-close state so
 * the card is reachable by mouse, keyboard, *and* click. Radix only mounts the
 * content subtree while the card is open or animating closed (via its `Presence`),
 * so data-fetching hooks inside consumers (e.g. wagmi) don't run until the card is
 * actually revealed — we rely on that rather than gating the children ourselves.
 *
 * Compound API:
 *
 * ```tsx
 * <EntityHoverCard>
 *   <EntityHoverCardTrigger asChild>…</EntityHoverCardTrigger>
 *   <EntityHoverCardContent>
 *     <EntityHoverCardHeader avatar={…} title={…} subtitle={…} actions={…} />
 *     <EntityHoverCardDivider />
 *     <EntityHoverCardRows>
 *       <EntityHoverCardRow label="Type" loading={…}>…</EntityHoverCardRow>
 *     </EntityHoverCardRows>
 *   </EntityHoverCardContent>
 * </EntityHoverCard>
 * ```
 */

/**
 * Delay before the card appears after the mouse enters the trigger. Matches the
 * Radix HoverCard defaults used by the previous Token/Market hover cards and
 * prevents accidental opens when the cursor merely passes over a trigger
 * (common in dense tables).
 */
const OPEN_DELAY_MS = 300
const CLOSE_DELAY_MS = 200

interface EntityHoverCardContextValue {
  open: boolean
  handleMouseEnter: () => void
  handleMouseLeave: () => void
}

const EntityHoverCardContext = createContext<EntityHoverCardContextValue | null>(null)

function useEntityHoverCard() {
  const ctx = useContext(EntityHoverCardContext)
  if (!ctx) {
    throw new Error('EntityHoverCard.* must be used inside <EntityHoverCard>')
  }
  return ctx
}

export function EntityHoverCard({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const openTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleMouseEnter = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }
    // Schedule the open — cancelled on early leave in handleMouseLeave. Keyboard
    // focus and click still open immediately via Popover's native trigger.
    if (!openTimeout.current) {
      openTimeout.current = setTimeout(() => {
        setOpen(true)
        openTimeout.current = null
      }, OPEN_DELAY_MS)
    }
  }

  const handleMouseLeave = () => {
    if (openTimeout.current) {
      clearTimeout(openTimeout.current)
      openTimeout.current = null
    }
    closeTimeout.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS)
  }

  return (
    <EntityHoverCardContext.Provider value={{ open, handleMouseEnter, handleMouseLeave }}>
      <Popover open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </EntityHoverCardContext.Provider>
  )
}

type EntityHoverCardTriggerProps = ComponentProps<typeof PopoverTrigger>

export function EntityHoverCardTrigger({ children, ...props }: EntityHoverCardTriggerProps) {
  const { handleMouseEnter, handleMouseLeave } = useEntityHoverCard()
  return (
    <PopoverTrigger {...props} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
    </PopoverTrigger>
  )
}

type EntityHoverCardContentProps = ComponentProps<typeof PopoverContent>

export function EntityHoverCardContent({
  children,
  className,
  side = 'top',
  align = 'center',
  sideOffset = 4,
  collisionPadding = 8,
  ...props
}: EntityHoverCardContentProps) {
  const { handleMouseEnter, handleMouseLeave } = useEntityHoverCard()
  return (
    <PopoverContent
      className={cn('w-72 space-y-3', className)}
      side={side}
      align={align}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onOpenAutoFocus={e => e.preventDefault()}
      onCloseAutoFocus={e => e.preventDefault()}
      onClick={e => e.stopPropagation()}
      {...props}
    >
      {/*
        Render children unconditionally — do NOT gate on `open`. Radix `Popover.Content`
        only mounts this subtree while the card is open or animating closed (Presence),
        so consumers' data-fetching hooks (e.g. wagmi) stay deferred until the first
        open regardless. Gating on `open` here unmounts the content the instant the
        close starts, leaving the shell to animate out empty (a brief empty-box flash).
      */}
      {children}
    </PopoverContent>
  )
}

interface EntityHoverCardHeaderProps {
  /** Leading icon or avatar. Rendered as aria-hidden — identity comes from `title`. */
  avatar?: ReactNode
  /** Primary identifier. Bold, truncates unless `wrap` is set. */
  title: ReactNode
  /** Optional secondary line underneath the title. Muted, truncates unless `wrap` is set. */
  subtitle?: ReactNode
  /** Optional trailing actions (copy buttons, external links, …). */
  actions?: ReactNode
  /** Wrap long title/subtitle text instead of truncating it. */
  wrap?: boolean
}

export function EntityHoverCardHeader({
  avatar,
  title,
  subtitle,
  actions,
  wrap = false
}: EntityHoverCardHeaderProps) {
  return (
    <div className="flex items-center gap-2">
      {avatar && (
        <span aria-hidden="true" className="shrink-0">
          {avatar}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <Text size="md" className={cn('font-semibold', wrap ? 'break-words' : 'truncate')}>
          {title}
        </Text>
        {subtitle && (
          <Text
            size="xs"
            className={cn('text-muted-foreground', wrap ? 'break-words' : 'truncate')}
          >
            {subtitle}
          </Text>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-0.5">{actions}</div>}
    </div>
  )
}

export function EntityHoverCardDivider() {
  return <Separator />
}

export function EntityHoverCardRows({ children, className, ...props }: ComponentProps<'dl'>) {
  return (
    <dl className={cn('space-y-1.5', className)} {...props}>
      {children}
    </dl>
  )
}

interface EntityHoverCardRowProps {
  /** Label shown on the left side. Typically a short noun. */
  label: ReactNode
  /** Value shown on the right side. Any node — text, badge, link, etc. */
  children: ReactNode
  /**
   * When `true`, shows a skeleton in place of `children` and crossfades to the
   * real value when it becomes `false`. When omitted, `children` is shown directly.
   */
  loading?: boolean
  /** Width hint for the default skeleton shown while `loading`. Defaults to `"w-16"`. */
  skeletonClassName?: string
  /**
   * Custom skeleton rendered while `loading`, in place of the default bar. Use when the
   * value isn't a single line of text (e.g. an avatar stack) so the loading and loaded
   * states share a footprint and crossfade cleanly. Match the loaded content's layout.
   */
  skeleton?: ReactNode
}

export function EntityHoverCardRow({
  label,
  children,
  loading,
  skeletonClassName = 'w-16',
  skeleton
}: EntityHoverCardRowProps) {
  const animationKey = loading ? 'loading' : 'loaded'
  return (
    <div className="flex min-h-7 items-center justify-between gap-2">
      <dt>
        <Text size="2xs" className="text-muted-foreground">
          {label}
        </Text>
      </dt>
      <dd className="min-w-0">
        <FadeSwap animationKey={animationKey}>
          {loading
            ? (skeleton ?? (
                <Skeleton
                  className={cn('h-4', skeletonClassName)}
                  aria-label={`Loading ${label}`}
                />
              ))
            : children}
        </FadeSwap>
      </dd>
    </div>
  )
}

function FadeSwap({ animationKey, children }: { animationKey: string; children: ReactNode }) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={animationKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}
